import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState } from
"react";
import debounce from "lodash.debounce";
import { quantityManagerEvents } from "./events";
import { QuantityManagerProps } from "./quantity-manager.types";
import styles from "./quantity-manager.module.css";
import { UiButton } from "@ui-monorepo/index";
import {
  DBProduct,
  getDbProductId,
  ProductType,
  useInventory,
  TypeInventory,
  getVariantByToppings,
  priceCalculator,
  priceWithWholesale } from
"@commons-monorepo/index";

export const QuantityManager: FC<QuantityManagerProps> = ({
  product,
  offerId,
  selectedToppings,
  logEvent,
  cartProducts,
  removeProduct,
  addProduct,
  theme,
  manuallyChangeProduct
}) => {
  const { maximumQuantity, canAddProduct } = useInventory({ cartProducts });
  const [quantityValue, setQuantityValue] = useState(0);

  const getProductDbId = useMemo(() => {
    const variant = getVariantByToppings(
      product.productToppingVariations || [],
      selectedToppings || []
    );
    const payload: DBProduct = {
      offerId,
      id: product.id,
      quantity: 1,
      toppings: selectedToppings || [],
      variationId: variant?.id,
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale
    };
    return getDbProductId(payload);
  }, [selectedToppings, product]);

  const cartProduct = useMemo((): DBProduct | undefined => {
    const dbId = getProductDbId;
    return cartProducts.find((p) => p.dbId === dbId);
  }, [cartProducts, getProductDbId]);

  const handleRemoveProductCart = (e?: MouseEvent<HTMLButtonElement>): void => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!!cartProduct?.dbId) {
      removeProduct(cartProduct);
    }
    logEvent &&
    logEvent({
      key: quantityManagerEvents.SELECT_SUBSTRACT_PRODUCT_QUANTITY
    });
  };

  const manuallyChange = (quantity: number): void => {
    const variant = getVariantByToppings(
      product.productToppingVariations || [],
      selectedToppings || []
    );
    let update: DBProduct = {
      id: product.id,
      quantity,
      toppings: selectedToppings || [],
      variationId: variant?.id,
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale
    };
    const maxQuantity = maximumQuantity(product, selectedToppings);

    if (
    !!maxQuantity &&
    quantity >= maxQuantity &&
    product.inventory?.type != TypeInventory.Build &&
    quantity)
    {
      update = { ...update, quantity: maxQuantity };
      setQuantityValue(maxQuantity);
    }

    manuallyChangeProduct(update);
  };

  const handleAddProductCart = (e?: MouseEvent<HTMLButtonElement>): void => {
    e?.stopPropagation();
    e?.preventDefault();
    if (product.type === ProductType.Service) {
      const hasAddedService = !!cartProducts.find((cp) => cp.id === product.id);
      if (hasAddedService) return;
    }

    let variationId;
    if (product.productToppingVariations) {
      const variant = getVariantByToppings(
        product.productToppingVariations,
        selectedToppings || []
      );
      variationId = variant?.id;
    }
    const payload: DBProduct = {
      id: product.id,
      quantity: 1,
      toppings: selectedToppings || [],
      price: product.priceWithDiscount || product.price,
      wholesale: product.wholesale,
      type: product.type,
      variationId
    };
    if (!!offerId) {
      payload.offerId = offerId;
    }
    if (canAddProduct(product, selectedToppings)) {
      addProduct(payload);
      logEvent &&
      logEvent({ key: quantityManagerEvents.SELECT_ADD_PRODUCT_QUANTITY });
    }
  };

  const lessIcon = useMemo(
    () => quantityValue === 1 ? "icon-trash" : "icon-minus",
    [quantityValue]
  );

  const handleManuallyChange = useCallback(
    debounce((quantity: number) => {
      manuallyChange(quantity);
    }, 250),
    []
  );

  useEffect(() => {
    setQuantityValue(cartProduct?.quantity || 0);
  }, [cartProduct, cartProducts]);

  return (
    <div className={styles.quantityManagerWrapper}>
      <UiButton
        aria-label='remove-product'
        className={styles.less}
        theme={theme}
        rightIcon={<span className={`${styles.icon} ${lessIcon}`} />}
        onClick={handleRemoveProductCart} />

      <input
        type={"number"}
        value={quantityValue}
        className={`${styles.number} ${
        !canAddProduct(product, selectedToppings) && styles.disabled
        }`}
        onClick={(e) => {
          e.stopPropagation();
          e?.preventDefault();
        }}
        onChange={(e) => handleManuallyChange(+(e.target?.value || 0))} />

      <UiButton
        hasPersonalization
        aria-label='add-product'
        className={styles.more}
        theme={theme}
        disabled={!canAddProduct(product, selectedToppings)}
        rightIcon={<span className={`${styles.icon} icon-plus`} />}
        onClick={handleAddProductCart} />

    </div>);

};